import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ScheduleIcon from "@mui/icons-material/Schedule";
import type { EmployeeAgreementFileResult } from "@services/riskAnalysis";

const MUTED = "#64748b";
const BORDER = "#d1d5db";
const TEAL_HEAD = "#3e829a";

const headSx = {
  fontWeight: 700,
  fontFamily: "Rethink Sans, sans-serif",
  color: "#fff",
  bgcolor: TEAL_HEAD,
  fontSize: "12px",
  borderColor: BORDER,
};

function statusIcon(row: EmployeeAgreementFileResult): React.ReactNode {
  switch (row.status) {
    case "processed":
      return <CheckCircleIcon sx={{ fontSize: 20, color: "#2e7d32" }} />;
    case "failed":
    case "empty":
      return <ErrorIcon sx={{ fontSize: 20, color: "#c62828" }} />;
    case "extracting":
      return <CircularProgress size={18} thickness={5} />;
    case "queued":
    default:
      return <ScheduleIcon sx={{ fontSize: 20, color: "#94a3b8" }} />;
  }
}

function statusLabel(row: EmployeeAgreementFileResult): string {
  switch (row.status) {
    case "processed":
      return "Processed";
    case "empty":
      return "No usable text";
    case "failed":
      return "Failed";
    case "extracting":
      return "Extracting…";
    case "queued":
      return "Queued";
    default:
      return row.status;
  }
}

/** Compact per-file extraction progress (Payor upload zone + shared patterns). */
export function ExtractionProgressTable(props: {
  rows: readonly EmployeeAgreementFileResult[];
}): React.ReactElement | null {
  const { rows } = props;
  if (!rows.length) return null;
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ mt: 1.5, borderColor: BORDER, maxHeight: 280 }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headSx, width: "44%" }}>File</TableCell>
            <TableCell sx={{ ...headSx, width: "14%" }} align="center">
              {/* icon */}
            </TableCell>
            <TableCell sx={{ ...headSx, width: "42%" }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const hasOcrPages =
              row.ocr_page != null &&
              row.ocr_total_pages != null &&
              row.ocr_total_pages > 0;
            const pageLine = hasOcrPages
              ? `Pages processed: ${row.ocr_page} / ${row.ocr_total_pages}`
              : null;
            const pageProgressPct =
              hasOcrPages && row.ocr_page != null && row.ocr_total_pages != null
                ? Math.min(
                    100,
                    Math.max(0, (row.ocr_page / row.ocr_total_pages) * 100),
                  )
                : null;
            const pdfHint =
              row.pdf_may_ocr &&
              (row.status === "queued" || row.status === "extracting") ? (
                <Typography
                  component="div"
                  variant="caption"
                  sx={{ color: MUTED, display: "block", mt: 0.25 }}
                >
                  PDF — text extraction may run OCR (slow)
                </Typography>
              ) : null;
            const startingExtract =
              row.status === "extracting" && !hasOcrPages ? (
                <Typography
                  component="div"
                  variant="caption"
                  sx={{ color: MUTED, display: "block", mt: 0.25 }}
                >
                  Starting text extraction…
                </Typography>
              ) : null;
            return (
              <TableRow key={`${row.filename}-${i}`}>
                <TableCell
                  sx={{
                    fontFamily: "Rethink Sans, sans-serif",
                    fontSize: "13px",
                    verticalAlign: "top",
                    wordBreak: "break-word",
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {row.filename}
                  </Box>
                  {pdfHint}
                  {startingExtract}
                  {pageLine ? (
                    <Typography
                      component="div"
                      variant="caption"
                      sx={{ color: MUTED, display: "block", mt: 0.25 }}
                    >
                      {pageLine}
                    </Typography>
                  ) : null}
                  {pageProgressPct != null ? (
                    <LinearProgress
                      variant="determinate"
                      value={pageProgressPct}
                      sx={{
                        mt: 0.75,
                        maxWidth: 280,
                        height: 6,
                        borderRadius: 1,
                        bgcolor: "#e2e8f0",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 1,
                          bgcolor: TEAL_HEAD,
                        },
                      }}
                    />
                  ) : null}
                </TableCell>
                <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                  {statusIcon(row)}
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "Rethink Sans, sans-serif",
                    fontSize: "13px",
                    verticalAlign: "middle",
                  }}
                >
                  {statusLabel(row)}
                  {row.status === "failed" || row.status === "empty" ? (
                    <Typography
                      component="div"
                      variant="caption"
                      sx={{ color: "#c62828", display: "block", mt: 0.25 }}
                    >
                      {row.error || ""}
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
